import { Payment, type Prices } from "@b2bportal/purchase-api";
import { ParentState } from "@checkout/types";
import type { State } from "xstate";
import {
  OfferPaymentState,
  TravelCreditPaymentState,
  type WalletPaymentParentContext,
} from "./types";

export type CommonStateType = State<WalletPaymentParentContext>;
export type CommonStateWithoutValue = Pick<CommonStateType, "context">;

export const getWalletCreditPayAmount = (state: CommonStateWithoutValue) =>
  state.context?.[TravelCreditPaymentState]?.creditPaymentAmount;

export const getWalletCreditBalance = (state: CommonStateWithoutValue) =>
  state.context?.[TravelCreditPaymentState]?.creditBalance;

export const getSelectedOffer = (state: CommonStateWithoutValue) =>
  state.context?.[OfferPaymentState]?.selectedOffer;

/**
 * The selected offer only while applied — use for pricing math.
 * `selectedOffer` persists after unchecking for tracking purposes only.
 */
export const getAppliedWalletOffer = (state: CommonStateWithoutValue) =>
  state.context?.[OfferPaymentState]?.offerApplied
    ? state.context?.[OfferPaymentState]?.selectedOffer
    : undefined;

export const getWalletOffers = (state: CommonStateWithoutValue) =>
  state.context?.[OfferPaymentState]?.offers ?? [];

export const getOfferApplied = (state: CommonStateWithoutValue) =>
  Boolean(state.context?.[OfferPaymentState]?.offerApplied);

export const getWalletOffersFetched = (state: CommonStateWithoutValue) =>
  Boolean(state.context?.[OfferPaymentState]?.fetchedOffers);

export const getWalletOfferError = (state: CommonStateWithoutValue) =>
  state.context?.[OfferPaymentState]?.error;

/**
 * The BE-computed offer discount from the latest quote breakdown.
 * Returns the breakdown's `paymentTotal` (the sum of all applied payments)
 * only when an offer is applied and a matching `TravelWalletOffer` entry
 * exists in the quoted payments. During checkout the only cart-level payment
 * is the offer, so `paymentTotal` equals the capped offer discount.
 */
export const getAppliedOfferDiscountAmount = (
  state: CommonStateWithoutValue
): Prices | undefined => {
  if (!getOfferApplied(state)) return undefined;

  const quoteBreakdown = state.context?.[ParentState.cartQuote]?.quoteBreakdown;
  if (quoteBreakdown == null) return undefined;

  const hasOfferPayment = quoteBreakdown.payments?.some(
    (p) => p.payment.type === Payment.TravelWalletOffer
  );

  return hasOfferPayment ? quoteBreakdown.paymentTotal : undefined;
};
